import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getInitialChecklistStatus } from '@/services/patients';
import { UserRole } from '@/types/auth';
import type { UserDTO } from '@/types/auth';
import { normalizeAuthRole } from '@/lib/postAuthSession';

type PatientOnboardingGateProps = {
  user: UserDTO | null;
  children: ReactNode;
};

// Cache key para evitar chamadas repetidas ao backend
const CHECKLIST_CACHE_KEY = (userId: string) => `checklist_status_${userId}`;

export function PatientOnboardingGate({ user, children }: PatientOnboardingGateProps) {
  const location = useLocation();
  // Inicia como false/false - não bloqueia até sabermos o status
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowChecklist, setShouldShowChecklist] = useState(false);
  const [checked, setChecked] = useState(false);
  const isPatient = normalizeAuthRole(user?.role) === UserRole.PATIENT;

  useEffect(() => {
    let isMounted = true;

    const loadStatus = async () => {
      if (!user?.id || !isPatient) {
        setChecked(true);
        return;
      }

      // Verifica cache local primeiro para evitar chamadas desnecessárias
      const cacheKey = CHECKLIST_CACHE_KEY(user.id);
      const cached = localStorage.getItem(cacheKey);
      if (cached !== null) {
        setShouldShowChecklist(cached === 'true');
        setChecked(true);
        return;
      }

      setIsLoading(true);

      try {
        const response = await getInitialChecklistStatus();
        if (!isMounted) return;

        if (response.success && response.data) {
          const show = response.data.shouldShowChecklist;
          setShouldShowChecklist(show);
          // Persiste no cache local (TTL via login/logout cycle)
          localStorage.setItem(cacheKey, String(show));
        } else {
          // Paciente recém-cadastrado sem cache: preferir checklist (fail-safe onboarding)
          console.warn('[OnboardingGate] Status indisponível; direcionando ao checklist.');
          setShouldShowChecklist(true);
        }
      } catch (err) {
        if (!isMounted) return;
        console.warn('[OnboardingGate] Erro ao verificar onboarding:', err);
        setShouldShowChecklist(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setChecked(true);
        }
      }
    };

    setChecked(false);
    loadStatus();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isPatient]);

  // Não é paciente: renderiza diretamente
  if (!user || !isPatient) {
    return <>{children}</>;
  }

  // Ainda verificando status
  if (isLoading || !checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // Precisa fazer checklist e não está na página dele
  if (shouldShowChecklist && location.pathname !== '/onboarding/checklist') {
    return <Navigate to="/onboarding/checklist" replace />;
  }

  // Já completou o checklist mas está tentando acessar a página dele
  if (!shouldShowChecklist && location.pathname === '/onboarding/checklist') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
